import { ReactNode } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Mic, Users, UserPlus, User as UserIcon } from "lucide-react";
import rcnLogo from "@/assets/rcn-logo.png";
import unionMeetingBg from "@/assets/union-chapter-1.jpg";
import washingtonMeetingBg from "@/assets/washington-chapter-1.jpg";
import type { SlotType } from "@/hooks/useWeeklySpeakers";

export interface SpeakerCardProps {
  chapter: "washington" | "union";
  slotType: SlotType | "open";
  member?: {
    name: string;
    company: string;
    title: string | null;
    image: string | null;
  } | null;
  topic?: string | null;
  dateLabel?: string;
  timeLabel?: string;
  locationLabel?: string;
  children?: ReactNode;
}

// Brand colors – chapter identity, not generic UI tokens.
const themes = {
  washington: {
    headerBg: "bg-[#1c4a8a]",
    bodyBg: "bg-white dark:bg-[#0c1a30]",
    bodyText: "text-[#0c1a30] dark:text-white",
    accent: "bg-[#c9a24a]",
    chipBg: "bg-white/15 text-white border border-white/30",
    label: "text-[#1c4a8a] dark:text-[#c9a24a]",
    border: "border-[#1c4a8a]/20",
    ring: "ring-[#c9a24a]",
  },
  union: {
    headerBg: "bg-white",
    bodyBg: "bg-white dark:bg-[#1a0a0a]",
    bodyText: "text-[#1a0a0a] dark:text-white",
    accent: "bg-[#c8102e]",
    chipBg: "bg-[#c8102e]/10 text-[#c8102e] border border-[#c8102e]/30",
    label: "text-[#c8102e]",
    border: "border-[#c8102e]/30",
    ring: "ring-[#c8102e]",
  },
} as const;

// ─────────────────────────────────────────────────────────────────
// Washington Member Spotlight – full portrait flyer
// ─────────────────────────────────────────────────────────────────
const WashingtonMemberSpotlight = ({
  member,
  dateLabel,
  timeLabel,
  locationLabel,
  topic,
  children,
}: {
  member: NonNullable<SpeakerCardProps["member"]>;
  dateLabel?: string;
  timeLabel?: string;
  locationLabel?: string;
  topic?: string | null;
  children?: ReactNode;
}) => {
  return (
    <div className="rounded-xl overflow-hidden border border-[#1c4a8a]/30 shadow-md">
      {/* Flyer surface: blue */}
      <div className="relative bg-[#1c4a8a] text-white aspect-square flex flex-col overflow-hidden [container-type:inline-size]">
        {/* Decorative gold offset frames behind portrait (bottom-right) */}
        <div className="pointer-events-none absolute right-4 bottom-4 w-[44%] aspect-square">
          <div className="absolute inset-0 translate-x-3 translate-y-3 border-2 border-[#c9a24a]/80 rotate-[4deg] rounded-sm" />
          <div className="absolute inset-0 translate-x-1 translate-y-1 border-2 border-[#c9a24a] -rotate-[3deg] rounded-sm" />
        </div>

        {/* Top row: date top-left, logo top-right */}
        <div className="flex justify-between items-center px-4 sm:px-5 pt-2 pb-0 gap-2">
          {dateLabel ? (
            <div className="text-white font-normal text-[clamp(0.7rem,3.6cqw,0.9rem)] leading-snug">
              {dateLabel}

            </div>
          ) : <div />}
          <div className="bg-white rounded px-2 py-1 shadow-sm">
            <img src={rcnLogo} alt="Referral Connection Network" className="h-7 sm:h-9 w-auto" />
          </div>
        </div>

        {/* Headline */}
        <div className="px-4 sm:px-5 pt-1">
          <h3 className="font-bold leading-tight text-white text-[clamp(1rem,6.4cqw,1.6rem)]">
            <span className="text-[#c9a24a]">Member Spotlight Presentation!</span>
          </h3>
          {locationLabel && (
            <div className="mt-1 text-white font-normal text-[clamp(0.7rem,3.6cqw,0.9rem)] leading-snug">
              {locationLabel}
            </div>
          )}
        </div>



        {/* Featured speaker block (bottom-left) + photo (bottom-right) */}
        <div className="mt-auto flex items-end gap-3 p-4 sm:p-5">
          <div className="relative z-10 flex-1 min-w-0 sm:[text-shadow:none] [text-shadow:0_1px_3px_rgba(12,26,48,0.85)]">
            <div className="text-white font-bold text-[clamp(0.9rem,5cqw,1.25rem)] leading-tight">
              Featured Speaker:
            </div>
            <div className="text-[#c9a24a] font-bold text-[clamp(0.85rem,4.8cqw,1.2rem)] leading-tight break-words">
              {member.name}
            </div>
            <div className="text-white/90 text-[clamp(0.65rem,3.4cqw,0.85rem)] mt-0.5 break-words">
              {member.title ? `${member.title}, ` : ""}{member.company}
            </div>
            {topic && (
              <div className="mt-2 text-white/90 text-[clamp(0.65rem,3.2cqw,0.8rem)] break-words">
                <span className="font-semibold text-[#c9a24a]">Topic: </span>

                {topic}
              </div>
            )}
          </div>

          {/* Portrait photo with gold frame */}
          <div className="relative w-[42%] aspect-square flex-shrink-0">
            <div className="absolute inset-0 overflow-hidden bg-white rounded-sm">
              {member.image ? (
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <UserIcon className="w-12 h-12 opacity-40" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Owner controls (if any) below the flyer */}
      {children && (
        <div className="bg-white dark:bg-[#0c1a30] px-4 py-3 border-t border-[#1c4a8a]/20">
          {children}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Compact card – used for Union + non-member slot types
// ─────────────────────────────────────────────────────────────────
const CompactCard = ({
  chapter,
  slotType,
  member,
  topic,
  dateLabel,
  children,
}: SpeakerCardProps) => {
  const t = themes[chapter];

  const headerLabel =
    slotType === "member" || slotType === "guest"
      ? "Featured Speaker"
      : slotType === "breakout"
      ? "Breakout Session"
      : "Speaker Slot Open";

  const Icon =
    slotType === "breakout" ? Users : slotType === "open" ? UserPlus : Mic;

  return (
    <div className={`rounded-xl overflow-hidden border ${t.border} shadow-sm`}>
      <div className={`${t.headerBg} px-4 py-3 flex items-center justify-between gap-3`}>
        <img
          src={rcnLogo}
          alt="Referral Connection Network"
          className="h-9 w-auto bg-white rounded px-2 py-1"
        />
        {dateLabel && (
          <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${t.chipBg}`}>
            {dateLabel}
          </span>
        )}
      </div>
      <div className={`h-1 ${t.accent}`} />
      <div className={`${t.bodyBg} ${t.bodyText} px-4 py-4`}>
        <div className={`text-[11px] font-bold uppercase tracking-[0.12em] ${t.label} flex items-center gap-1.5`}>
          <Icon className="w-3.5 h-3.5" />
          {headerLabel}
        </div>

        {slotType === "member" && member ? (
          <div className="mt-3 flex items-start gap-3">
            <Avatar className={`h-16 w-16 ring-2 ring-offset-2 ring-offset-background ${t.ring} flex-shrink-0`}>
              {member.image && <AvatarImage src={member.image} alt={member.name} className="object-cover" />}
              <AvatarFallback className="bg-muted">
                <UserIcon className="w-6 h-6 opacity-50" />
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-base font-bold leading-tight break-words">{member.name}</div>
              <div className="text-sm opacity-80 break-words">
                {member.title ? `${member.title}, ` : ""}{member.company}
              </div>
              {topic ? (
                <div className="mt-2 text-sm break-words">
                  <span className="font-semibold">Topic: </span>
                  <span className="opacity-90">{topic}</span>
                </div>
              ) : (
                <div className="mt-2 text-sm italic opacity-60">Topic TBA</div>
              )}
            </div>
          </div>
        ) : slotType === "guest" ? (
          <div className="mt-3">
            <div className="text-base font-bold leading-tight break-words">
              {topic || <span className="italic opacity-60">Guest speaker TBA</span>}
            </div>
            <div className="text-xs opacity-70 mt-0.5">Guest Speaker</div>
          </div>
        ) : slotType === "breakout" ? (
          <div className="mt-3 text-sm break-words">
            {topic ? (
              <>
                <span className="font-semibold">Topic: </span>
                <span className="opacity-90">{topic}</span>
              </>
            ) : (
              <span className="italic opacity-70">Member-led breakout discussion</span>
            )}
          </div>
        ) : (
          <div className="mt-3 text-sm opacity-80">
            This date is open. Members can sign up to be the featured speaker.
          </div>
        )}

        {children && <div className="mt-4">{children}</div>}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Union Member Spotlight – full portrait flyer (red on white)
// ─────────────────────────────────────────────────────────────────
const UnionMemberSpotlight = ({
  member,
  dateLabel,
  timeLabel,
  locationLabel,
  topic,
  children,
}: {
  member: NonNullable<SpeakerCardProps["member"]>;
  dateLabel?: string;
  timeLabel?: string;
  locationLabel?: string;
  topic?: string | null;
  children?: ReactNode;
}) => {
  const dateLine = [dateLabel, timeLabel].filter(Boolean).join(" • ");
  return (
    <div className="rounded-xl overflow-hidden border border-[#c8102e]/30 shadow-md">
      {/* Flyer surface: white */}
      <div className="relative bg-white text-[#c8102e] aspect-square flex flex-col overflow-hidden [container-type:inline-size]">
        {/* Black Z-shape: rendered as inline SVG so html2canvas-pro can snapshot
            it reliably (CSS clip-path is not supported by the snapshotter). */}
        <svg
          className="pointer-events-none absolute inset-0 w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <polygon
            fill="black"
            points="85.3,30 100,30 100,100 0,100 0,84 79.4,84"
          />
        </svg>

        {/* Top row: date top-left, logo top-right */}
        <div className="relative z-10 flex justify-between items-center pt-4 sm:pt-5 px-5 sm:px-6 gap-2">
          {dateLabel ? (
            <div className="text-[#c8102e] font-bold text-[clamp(0.7rem,3.8cqw,0.95rem)] leading-snug">
              {dateLabel}
            </div>
          ) : <div />}
          <img
            src={rcnLogo}
            alt="Referral Connection Network"
            className="h-7 sm:h-9 w-auto"
          />
        </div>

        {/* Headline */}
        <div className="relative z-10 px-5 sm:px-6 pt-[clamp(0.6rem,3cqw,1rem)]">
          <h3 className="font-extrabold leading-[1.06] text-[#c8102e] text-[clamp(1rem,5.6cqw,1.25rem)]">
            <span className="hidden lg:inline">Join us for our </span>Member<br className="hidden lg:inline" /> Spotlight Presentation!
          </h3>
          {locationLabel && (
            <div className="mt-1 max-w-[80%] text-[#c8102e] font-normal text-[clamp(0.7rem,3.8cqw,0.95rem)] leading-snug [overflow-wrap:anywhere]">
              {locationLabel}
            </div>
          )}

        </div>



        {/* Featured speaker block — kept in white area, clear of black */}
        <div className="absolute z-10 left-5 sm:left-6 top-[53%] w-[50%] max-w-[50%] min-w-0">
          <div className="text-[#c8102e] font-extrabold leading-[1.08] text-[clamp(0.75rem,4.8cqw,1rem)]">
            Featured Speaker:
          </div>
          <div className="text-[#c8102e] font-extrabold leading-[1.08] [overflow-wrap:anywhere] text-[clamp(0.8rem,5cqw,1.1rem)]">
            {member.name}
          </div>
          <div className="text-[#c8102e] mt-1 max-w-full leading-[1.2] [overflow-wrap:anywhere] font-medium text-[clamp(0.62rem,3.3cqw,0.78rem)]">
            {member.title ? `${member.title}, ` : ""}{member.company}
          </div>
          {topic && (
            <div className="mt-1.5 text-[#c8102e] leading-[1.18] [overflow-wrap:anywhere] text-[clamp(0.58rem,3cqw,0.72rem)]">
              <span className="font-semibold">Topic: </span>
              {topic}
            </div>
          )}
        </div>


        <div className="flex-1" />

        {/* Circular portrait with white border, anchored near bottom-right over black */}
        <div className="absolute right-[5%] bottom-[3%] w-[34%] aspect-square z-10">
          <div className="absolute inset-0 rounded-full bg-white p-[4px] shadow-lg">
            <div className="w-full h-full rounded-full overflow-hidden bg-white">
              {member.image ? (
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <UserIcon className="w-12 h-12 opacity-40" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Owner controls (if any) below the flyer */}
      {children && (
        <div className="bg-white dark:bg-[#1a0a0a] px-4 py-3 border-t border-[#c8102e]/20">
          {children}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Union Breakout Session – full square flyer with photo background
// ─────────────────────────────────────────────────────────────────
const UnionBreakout = ({
  topic,
  dateLabel,
  timeLabel,
  locationLabel,
  children,
}: {
  topic?: string | null;
  dateLabel?: string;
  timeLabel?: string;
  locationLabel?: string;
  children?: ReactNode;
}) => {
  return (
    <div className="rounded-xl overflow-hidden border border-[#c8102e]/30 shadow-md">
      <div className="relative aspect-square flex flex-col justify-center pt-[10%] sm:pt-[8%] lg:pt-[4%] overflow-hidden bg-neutral-700">
        {/* Grayscale photo background */}
        <img
          src={unionMeetingBg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover grayscale"
        />
        {/* Light gray overlay to soften the photo */}
        <div className="absolute inset-0 bg-neutral-500/55" />
        {/* Slight red tint */}
        <div className="absolute inset-0 bg-[#c8102e]/50 mix-blend-multiply" />

        {/* Logo top-right */}
        <div className="absolute z-10 top-3 right-3 sm:top-4 sm:right-4">
          <div className="bg-white/95 rounded px-2 py-1 shadow-sm">
            <img
              src={rcnLogo}
              alt="Referral Connection Network"
              className="h-7 sm:h-9 w-auto"
            />
          </div>
        </div>

        {/* Headline */}
        <div className="relative z-10 px-5 sm:px-6 text-center">
          <h3
            className="font-extrabold leading-[1.05] text-white text-[clamp(1rem,2.8vw,2rem)]"
            style={{ textShadow: "0 2px 6px rgba(0,0,0,0.45)" }}
          >
            Breakout Session
          </h3>
          {locationLabel && (
            <div
              className="mt-1 font-medium text-white/90 leading-snug text-[clamp(0.6rem,1.3vw,0.85rem)]"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
            >
              {locationLabel}
            </div>
          )}
        </div>

        {/* Date + Time */}
        {dateLabel && (
          <div
            className="relative z-10 px-5 sm:px-6 mt-2 sm:mt-3 lg:mt-4 text-center text-white font-bold leading-snug text-[clamp(0.7rem,1.5vw,1.05rem)]"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
          >
            <div>{dateLabel}</div>
            {timeLabel && (
              <div className="font-semibold text-white/95 text-[clamp(0.62rem,1.3vw,0.9rem)]">
                {timeLabel}
              </div>
            )}
          </div>
        )}

        {/* Italic description + optional topic, right below the date */}
        <div className="relative z-10 mt-2 sm:mt-3 lg:mt-3 px-4 sm:px-6 lg:px-8 pb-4 sm:pb-5 text-center">
          <p
            className="italic font-semibold text-white leading-snug text-[clamp(0.62rem,1.3vw,0.95rem)]"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
          >
            Join us for our Member-led breakout discussion
          </p>
          {topic && (
            <p
              className="mt-3 sm:mt-4 font-bold text-white leading-snug text-[clamp(0.62rem,1.3vw,0.95rem)]"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
            >
              <span className="font-semibold opacity-90">Topic: </span>
              {topic}
            </p>
          )}
        </div>



      </div>

      {children && (
        <div className="bg-white dark:bg-[#1a0a0a] px-4 py-3 border-t border-[#c8102e]/20">
          {children}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Washington Breakout Session – full square flyer with photo background
// ─────────────────────────────────────────────────────────────────
const WashingtonBreakout = ({
  topic,
  dateLabel,
  timeLabel,
  locationLabel,
  children,
}: {
  topic?: string | null;
  dateLabel?: string;
  timeLabel?: string;
  locationLabel?: string;
  children?: ReactNode;
}) => {
  return (
    <div className="rounded-xl overflow-hidden border border-[#1c4a8a]/30 shadow-md">
      <div className="relative aspect-square flex flex-col justify-center pt-[10%] sm:pt-[8%] lg:pt-[4%] overflow-hidden bg-neutral-700">
        {/* Grayscale photo background */}
        <img
          src={washingtonMeetingBg}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover grayscale"
        />
        {/* Light gray overlay to soften the photo */}
        <div className="absolute inset-0 bg-neutral-500/55" />
        {/* Blue tint */}
        <div className="absolute inset-0 bg-[#1c4a8a]/85 mix-blend-multiply" />

        {/* Logo top-right */}
        <div className="absolute z-10 top-3 right-3 sm:top-4 sm:right-4">
          <div className="bg-white/95 rounded px-2 py-1 shadow-sm">
            <img
              src={rcnLogo}
              alt="Referral Connection Network"
              className="h-7 sm:h-9 w-auto"
            />
          </div>
        </div>

        {/* Headline */}
        <div className="relative z-10 px-5 sm:px-6 text-center">
          <h3
            className="font-extrabold leading-[1.05] text-[#c9a24a] text-[clamp(1rem,2.8vw,2rem)]"
            style={{ textShadow: "0 2px 6px rgba(0,0,0,0.45)" }}
          >
            Breakout Session
          </h3>
          {locationLabel && (
            <div
              className="mt-1 font-medium text-white/90 leading-snug text-[clamp(0.6rem,1.3vw,0.85rem)]"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
            >
              {locationLabel}
            </div>
          )}
        </div>

        {/* Date + Time */}
        {dateLabel && (
          <div
            className="relative z-10 px-5 sm:px-6 mt-2 sm:mt-3 lg:mt-4 text-center text-[#c9a24a] font-bold leading-snug text-[clamp(0.7rem,1.5vw,1.05rem)]"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
          >
            <div>{dateLabel}</div>
            {timeLabel && (
              <div className="font-semibold text-[#c9a24a]/95 text-[clamp(0.62rem,1.3vw,0.9rem)]">
                {timeLabel}
              </div>
            )}
          </div>
        )}


        {/* Italic description + optional topic */}
        <div className="relative z-10 mt-2 sm:mt-3 lg:mt-3 px-4 sm:px-6 lg:px-8 pb-4 sm:pb-5 text-center">
          <p
            className="italic font-semibold text-white leading-snug text-[clamp(0.62rem,1.3vw,0.95rem)]"
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
          >
            Join us for our Member-led breakout discussion
          </p>
          {topic && (
            <p
              className="mt-3 sm:mt-4 font-bold text-white leading-snug text-[clamp(0.62rem,1.3vw,0.95rem)]"
              style={{ textShadow: "0 1px 4px rgba(0,0,0,0.5)" }}
            >
              <span className="font-semibold opacity-90">Topic: </span>
              {topic}
            </p>
          )}
        </div>
      </div>

      {children && (
        <div className="bg-white dark:bg-[#0c1a30] px-4 py-3 border-t border-[#1c4a8a]/20">
          {children}
        </div>
      )}
    </div>
  );
};

const SpeakerCard = (props: SpeakerCardProps) => {
  if (props.slotType === "member" && props.member) {
    if (props.chapter === "washington") {
      return (
        <WashingtonMemberSpotlight
          member={props.member}
          dateLabel={props.dateLabel}
          timeLabel={props.timeLabel}
          locationLabel={props.locationLabel}
          topic={props.topic}
        >
          {props.children}
        </WashingtonMemberSpotlight>
      );
    }
    return (
      <UnionMemberSpotlight
        member={props.member}
        dateLabel={props.dateLabel}
        timeLabel={props.timeLabel}
        locationLabel={props.locationLabel}
        topic={props.topic}
      >
        {props.children}
      </UnionMemberSpotlight>
    );
  }
  if (props.slotType === "breakout") {
    if (props.chapter === "washington") {
      return (
        <WashingtonBreakout
          topic={props.topic}
          dateLabel={props.dateLabel}
          timeLabel={props.timeLabel}
          locationLabel={props.locationLabel}
        >
          {props.children}
        </WashingtonBreakout>
      );
    }
    return (
      <UnionBreakout
        topic={props.topic}
        dateLabel={props.dateLabel}
        timeLabel={props.timeLabel}
        locationLabel={props.locationLabel}
      >
        {props.children}
      </UnionBreakout>
    );
  }
  return <CompactCard {...props} />;
};

export default SpeakerCard;
